import { Box, Button, Card, CardContent, Checkbox, FormHelperText, Grid, Stack, Typography } from "@mui/material";
import { ListContextProvider, ReactAdminComponentPropsWithId, useListContext } from "ra-core"
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import { Link as RouterLink } from 'react-router-dom'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { Content } from "~/components/content/Content"
import { useSimpleSaveAction } from "~/controller/SaveContextProvider"
import { RecPatch } from "~/dataProvider/tools"
import { OdResourcePageProps } from "~/types/resource"
import { Datagrid } from "~/components/list/datagrid";
import Scrollbar from "~/components/Scrollbar";
import { FunctionField, TextField as DgTextField } from "~/components/field";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import Create from "~/controller/Create";
import { CreateDataAccessRoleInput, PrimaryClassification, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import useLocalListController from "~/components/list/useLocalListController";
import { classificationListOptions } from "./DataClassifications";
import usePermitGuard from "~/authProvider/usePermitGuard";
import { MANAGE_DATA_ACCESS_ROLES } from "~/constants";
import { debounce } from "lodash";


const CreateDataAccessRole: FC<ReactAdminComponentPropsWithId> = (props) => {
  usePermitGuard([[MANAGE_DATA_ACCESS_ROLES]])
  const listCtrl = useLocalListController({ resource: 'settings/governance/data-classification', basePath: '', filter: { }, perPage: 10000 }, classificationListOptions)

  const dispatch = useDispatch()
  const goList = useCallback(() => dispatch(push(`/${props.resource}`)), [dispatch, props.resource])
  const handlers = useDefaultSavedHandlers('Create Data Access Role')
  handlers.onSuccess.merge(goList)
  return (
    <ListContextProvider value={listCtrl}>
      <Create {...props} {...handlers} >
        <CreateDataAccessRoleBody {...props} />
      </Create>
    </ListContextProvider>
  )
}

type ClassificationDto = SecondaryClassificationResponseDto;

const validationSchema =
  Yup.object().shape({
    roleName: Yup.string().max(100, 'Role Name must be at most 100 characters').required('Role Name is required'),
    roleValue: Yup.string().max(100, 'Role Value must be at most 100 characters').required('Role Value is required').matches(/^\s*\S*\s*$/, 'Role Value should not contain white spaces'),
    description: Yup.string().max(512),
  })

const CreateDataAccessRoleBody: FC<OdResourcePageProps> = ({
  resource,
  options,
}) => {
  const save = useSimpleSaveAction()
  
  const initialValues = {
    submit: '',
    roleName: '',
    roleValue: '',
    description: '',
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const roleGrants = Object.keys(grantMapRef.current).reduce((acc, k) => {
      const entry = grantMapRef.current[k]
      const clsId = Number.parseInt(k)
      if (entry.read)  { acc.readGrants .push(clsId) }
      if (entry.write) { acc.writeGrants.push(clsId) }
      return acc
    }, { readGrants: [] as number[], writeGrants: [] as number[] })

    const toSave: CreateDataAccessRoleInput = {
      name: values.roleName,
      value: values.roleValue,
      description: values.description,
      ...roleGrants,
    }
    save(toSave, options)
  }, [save])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, touched, isSubmitting, submitForm, setFormikState, handleChange: formikOnChange, validateField } = formikCtrl
  
  const listCtx = useListContext<ClassificationDto & RecPatch>()
  const { data, setSort } = listCtx
  const setSortRef = useRef(setSort)
  setSortRef.current = setSort

  const [grantUpdate, setGrantUpdate] = useState(0)
  const bumpVer = (n:number) => n + 1

  const {current:innerState} = useRef({
    allReadChecked: false, 
    readsIndeterminate: false, 
    allWriteChecked: false, 
    writeIndeterminate: false,
  })
  const prevStateSignature = useRef(JSON.stringify(innerState))
  const calculateInnerState = () => {
    const values = Object.values(grantMapRef.current)
    innerState.allReadChecked  = values.every(v => v.read)
    innerState.allWriteChecked = values.every(v => v.write)
    innerState.readsIndeterminate = values.some(v => v.read) && values.some(v => !v.read)
    innerState.writeIndeterminate = values.some(v => v.write) && values.some(v => !v.write)
    const signature = JSON.stringify(innerState)
    if (signature !== prevStateSignature.current) {
      setTimeout(() => setSortRef.current('id', signature), 10)
    }
    prevStateSignature.current = signature
  }

  const grantMapRef = useRef<Record<string, {read:boolean,write:boolean}>>({})
  const dataLoaded = useRef(false)
  useEffect(() => {
    if (Object.keys(data).length > 0 && !dataLoaded.current) {
      grantMapRef.current = Object.keys(data || {}).reduce((acc, k) => {
        const cls = data[k]
        acc[k] = { 
          read: cls.primaryClassification === PrimaryClassification.Public,
          write: false,
        };
        return acc
      }, {} as Record<string, {read:boolean,write:boolean}>)
      calculateInnerState()
      setGrantUpdate(bumpVer)
      dataLoaded.current = true
    }
  }, [data])
  
  const actionRef = useRef((s:string) => {})
  const debounceAction = useMemo(() => debounce((s: string) => actionRef.current(s), 1500), [])

  const handleDisplayNameChange = useMemo(() => {
    const autoTrim = (s:string) => {
      const trimed = s && typeof(s) === 'string' ? s.trim() : s
      formikOnChange({target:{value:trimed, name: 'roleName'}})
    }
    actionRef.current = autoTrim
    if (!touched.roleValue) {
      const handleChangeAndPopulate = (e: ChangeEvent<any>) => {
        const v0 : string = e.target.value
        setFormikState(v => ({...v,
          values: {...v.values,
            roleName: v0,
            roleValue: v0.trim().replace(/\s/g, '')
          },
          touched: {...v.touched, roleName: true },
        }))
        validateField('roleName')
        debounceAction(v0)
      }
      return handleChangeAndPopulate

    } else {        
      const handleChange = (e: ChangeEvent<any>) => {
        formikOnChange(e)
        debounceAction(e.target.value)
      }
      return handleChange
    }
  }, [debounceAction, formikOnChange, setFormikState, touched.roleValue, validateField])


  const handleGrantChange = useCallback((d:ClassificationDto&RecPatch, grantName:'read'|'write') => (e:React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    grantMapRef.current[d.id][grantName] = checked
    if (grantName === 'write' && checked) {
      grantMapRef.current[d.id]['read'] = true
    }
    if (grantName === 'read' && !checked) {
      grantMapRef.current[d.id]['write'] = false
    }
    calculateInnerState()
    setGrantUpdate(bumpVer)
  }, [])

  const handleSelectAll = (grantName: 'read'|'write') => (e:React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    if (grantName === 'read') {
      if (checked) {
        for(let v of Object.values(grantMapRef.current)) {
          v.read = true
        }
      } else {
        for(let dId of Object.keys(grantMapRef.current)) {
          const d = data && data[dId]
          if (!d || d.primaryClassification === PrimaryClassification.Public) {continue}
          const v = grantMapRef.current[dId]
          v.read = false
          v.write = false
        }
      }
    } else {
      if (checked) {
        for(let v of Object.values(grantMapRef.current)) {
          v.write = true
          v.read = true
        }
      } else {
        for(let v of Object.values(grantMapRef.current)) {
          v.write = false
        }
      }
    }
    calculateInnerState()
    setGrantUpdate(bumpVer)
  }

  return (
    <Content 
      pageName="Create Data Access Role" 
      resourceName="Create" 
      mainActions={
        <>
          <Button
            color="primary"
            disabled={isSubmitting}
            variant="contained"
            onClick={submitForm}
          >
            Save
          </Button>
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardContent>
            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} md={6} >
                <TextField label="Role Name*" name="roleName" onChange={handleDisplayNameChange} />
              </Grid>
              <Grid item xs={12} md={6} >
                <TextField label="Value*" name="roleValue" />
              </Grid>
              <Grid item xs={12} md={12} >
                <TextField label="Description" name="description" multiline={true} />
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
        </Card>
            
        {data &&
          <Card sx={{ mt: 4 }}>
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid 
                  hasBulkActions={false} 
                  sx={{ 
                    '& th' : { py: 2, '&:nth-of-type(2)': { p: 0 } },
                    '& td' : { py: 2, '&:nth-of-type(2)': { p: 0 } },
                    '& .name': { maxWidth: 150, display: 'inline-block', wordBreak: 'break-word' },
                    '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
                  }}
                  {...listCtx}
                >
                  <FunctionField 
                    label={
                      <Stack direction="column" alignItems="center">
                        <span>Read Access</span>
                        <Checkbox 
                          checked={innerState.allReadChecked} 
                          indeterminate={innerState.readsIndeterminate} 
                          onChange={handleSelectAll('read')} 
                          inputProps={{ 'aria-label': 'Check All Read Grants' }}
                        />
                      </Stack>
                    }
                    sortable={false} 
                    render={(d?:ClassificationDto&RecPatch) => (
                      d ?
                      <Checkbox 
                        disabled={d.primaryClassification === PrimaryClassification.Public}
                        checked={grantMapRef.current[d.id]?.read || false}
                        onChange={handleGrantChange(d, 'read')}
                        inputProps={{ 'aria-label': `Grant Read Access` }}
                      /> : null
                    )} 
                    textAlign="center"
                  />
                  <FunctionField 
                    label={
                      <Stack direction="column" alignItems="center">
                        <span>Write Access</span>
                        <Checkbox 
                          checked={innerState.allWriteChecked} 
                          indeterminate={innerState.writeIndeterminate} 
                          onChange={handleSelectAll('write')} 
                          inputProps={{ 'aria-label': 'Check All Write Grants' }}
                        />
                      </Stack>
                    }
                    sortable={false} 
                    render={(d?:ClassificationDto&RecPatch) => (
                      d ?
                      <Checkbox 
                        checked={grantMapRef.current[d.id]?.write || false}
                        onChange={handleGrantChange(d, 'write')}
                        inputProps={{ 'aria-label': `Grant Write Access` }}
                      /> : null
                    )} 
                    textAlign="center"
                  />
                  <DgTextField 
                    source="primaryClassificationText" 
                    label={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Primary</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Classification</Typography>
                      </Box>
                    }
                    sortable={false}
                  />
                  <DgTextField 
                    source="name" 
                    label={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Secondary</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Classification</Typography>
                      </Box>
                    }
                    sortable={false} 
                    className="name" 
                  />
                  <DgTextField source="description" label="Description" sortable={false} className="description" />
                </Datagrid>
              </Box>
            </Scrollbar>
          </Card>
        }

      </OdForm>
    </Content>
  )
}

export default CreateDataAccessRole;
